import type { SRDItem } from "../types/srd.js";
import type {
  Item,
  Weapon,
  Armor,
  Spellbook,
  ItemCategory,
  WeaponProperty,
  EquipmentSlot,
} from "../types/items.js";
import type { DamageType } from "../types/combat.js";

const EQUIPMENT_SLOTS: readonly EquipmentSlot[] = [
  "head",
  "torso",
  "gloves",
  "boots",
  "necklace",
  "ring1",
  "ring2",
  "mainHand",
  "offHand",
];

function resolveSlot(raw: SRDItem): EquipmentSlot | undefined {
  if (raw.slot === undefined) return undefined;
  return EQUIPMENT_SLOTS.includes(raw.slot as EquipmentSlot)
    ? (raw.slot as EquipmentSlot)
    : undefined;
}
import { dataRegistry } from "./DataRegistry.js";
import { parseDiceNotation } from "../combat/dice.js";

// ── Item resolver ─────────────────────────────────────────────────────────────

/** Union of all concrete runtime item types returned by resolveItem. */
export type GameItem = Item | Weapon | Armor | Spellbook;

/**
 * Convert a raw bkcrawl item JSON record into the appropriate runtime type.
 *
 * Dispatch logic:
 *   - tags includes "weapon" AND damage block present → Weapon
 *   - tags includes "armor"  AND armor  block present → Armor
 *   - otherwise → Item (category derived from first recognised tag)
 *
 * Note: value in JSON is gold pieces; runtime valueCp is copper pieces (×100).
 */
export function resolveItem(raw: SRDItem): GameItem {
  const slot = resolveSlot(raw);
  // Carry the authored consumable block through when it's an effect the
  // engine can execute (currently: heal). Unknown effects are dropped so
  // the Use button never offers something the engine can't do.
  const consumable =
    raw.consumable?.effect === "heal" && typeof raw.consumable.magnitude === "number"
      ? { effect: "heal" as const, magnitude: raw.consumable.magnitude }
      : undefined;

  const base = {
    id: raw.index,
    name: raw.name,
    weightLbs: raw.weight,
    valueCp: raw.value * 100,
    description: raw.description,
    tags: [...raw.tags] as readonly string[],
    stackable: raw.stackable,
    ...(slot !== undefined ? { slot } : {}),
    ...(consumable !== undefined ? { consumable } : {}),
  };

  if (raw.tags.includes("weapon") && raw.damage) {
    const { count, sides } = parseDiceNotation(raw.damage.dice);
    const dice = { count, sides };
    const weapon: Weapon = {
      ...base,
      category: "weapon",
      damage: dice,
      damageType: raw.damage.type as DamageType,
      properties: (raw.damage.properties ?? []) as WeaponProperty[],
      abilityScore: raw.damage.ability ?? "STR",
    };
    return weapon;
  }

  if (raw.tags.includes("armor") && raw.armor) {
    const armor: Armor = {
      ...base,
      category: "armor",
      baseAC: raw.armor.base_ac,
      addDex: raw.armor.add_dex,
      maxDexBonus: raw.armor.max_dex_bonus ?? null,
      stealthDisadvantage: raw.armor.stealth_disadvantage ?? false,
      strengthRequirement: raw.armor.strength_requirement ?? 0,
    };
    return armor;
  }

  const RECOGNISED: ItemCategory[] = ["potion", "scroll", "weapon", "armor", "misc"];
  const category: ItemCategory =
    (raw.tags.find((t): t is ItemCategory => RECOGNISED.includes(t as ItemCategory))) ?? "misc";

  if (category === "misc" && raw.tags.includes("spellbook")) {
    const spellbook: Spellbook = { ...base, category: "misc", isSpellbook: true };
    return spellbook;
  }

  const item: Item = { ...base, category };
  return item;
}

// ── Convenience async loader ──────────────────────────────────────────────────

/**
 * Fetch an item from the DataRegistry and resolve it to a runtime type.
 * Returns null if the item JSON does not exist.
 */
export async function loadItem(id: string): Promise<GameItem | null> {
  const raw = await dataRegistry.getItem(id);
  if (!raw) return null;
  return resolveItem(raw);
}
